import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Brain, ArrowLeft, Loader2, Sparkles } from 'lucide-react';
import { AuthContext } from '../App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConceptForm = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    num_simulations: 100
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      // Create concept
      const conceptRes = await axios.post(
        `${API}/concepts`,
        {
          name: formData.name,
          description: formData.description,
          image_url: formData.image_url || null
        },
        { withCredentials: true }
      );

      const concept = conceptRes.data;

      // Start simulation
      const simRes = await axios.post(
        `${API}/simulations/run`,
        {
          product_concept_id: concept.id,
          num_simulations: formData.num_simulations
        },
        { withCredentials: true }
      );

      toast.success('Simulation started! Running AI consumer tests...');
      navigate(`/simulation/${simRes.data.simulation_id}`);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 500 || error.response?.data?.detail?.includes('OPENAI_API_KEY')) {
        toast.error('OpenAI API key not configured. Please add your API key to the backend .env file.');
      } else {
        toast.error('Failed to create concept and start simulation');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {/* Navigation */}
      <nav className="border-b border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Brain className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-cyan-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              IntentAI
            </span>
          </div>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            data-testid="back-to-dashboard-button"
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>New Product Concept</h1>
          <p className="text-slate-600">Upload your concept to start AI-powered consumer testing</p>
        </div>

        <Card className="border-slate-200 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-indigo-600" />
              Concept Details
            </CardTitle>
            <CardDescription>Provide information about your product or marketing concept</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="concept-form">
              <div>
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  data-testid="product-name-input"
                  placeholder="e.g., EcoBottle Smart Water Bottle"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Product Description *</Label>
                <Textarea
                  id="description"
                  data-testid="product-description-input"
                  placeholder="Describe your product, its key features, benefits, and target audience. This is what AI consumers will evaluate."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-2 min-h-[150px]"
                  required
                />
              </div>

              <div>
                <Label htmlFor="image_url">Product Image URL (Optional)</Label>
                <Input
                  id="image_url"
                  data-testid="product-image-input"
                  placeholder="https://example.com/product-image.jpg"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="num_simulations">Number of Simulations</Label>
                <Select
                  value={formData.num_simulations.toString()}
                  onValueChange={(value) => setFormData({ ...formData, num_simulations: parseInt(value) })}
                >
                  <SelectTrigger className="mt-2" data-testid="num-simulations-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50" data-testid="simulations-50">50 simulations (Quick test)</SelectItem>
                    <SelectItem value="100" data-testid="simulations-100">100 simulations (Standard)</SelectItem>
                    <SelectItem value="500" data-testid="simulations-500">500 simulations (Comprehensive)</SelectItem>
                    <SelectItem value="1000" data-testid="simulations-1000">1000 simulations (Deep dive)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-slate-500 mt-2">
                  More simulations = more accurate results (but takes longer)
                </p>
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  data-testid="cancel-button"
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="start-simulation-button"
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 text-white rounded-full font-semibold"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Starting Simulation...
                    </>
                  ) : (
                    'Start AI Simulation'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConceptForm;